'use client';

import { AlertCircle, RefreshCw, Home, HelpCircle } from 'lucide-react';
import { Button } from './Button';

interface UserFriendlyErrorProps {
  title?: string;
  message: string;
  type?: 'network' | 'not-found' | 'invalid' | 'unknown';
  onRetry?: () => void;
  onGoHome?: () => void;
}

export function UserFriendlyError({ 
  title = 'Ups, da ist etwas schiefgelaufen!',
  message, 
  type = 'unknown',
  onRetry,
  onGoHome
}: UserFriendlyErrorProps) {
  // Choose icon based on error type
  const getIcon = () => {
    switch (type) {
      case 'network':
        return <AlertCircle className="h-12 w-12 text-orange-500" />;
      case 'not-found':
        return <HelpCircle className="h-12 w-12 text-blue-500" />;
      case 'invalid':
        return <AlertCircle className="h-12 w-12 text-yellow-500" />;
      default:
        return <AlertCircle className="h-12 w-12 text-red-500" />;
    }
  };

  // Get helpful suggestion based on error type
  const getSuggestion = () => {
    switch (type) {
      case 'network':
        return 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.';
      case 'not-found':
        return 'Versuchen Sie es mit einer anderen Gemeinde oder kontaktieren Sie uns für Hilfe.';
      case 'invalid':
        return 'Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut.';
      default:
        return 'Bitte versuchen Sie es später erneut oder kontaktieren Sie unseren Support.';
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="mb-4">
        {getIcon()}
      </div>
      
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        {title}
      </h2>
      
      <p className="text-gray-600 mb-4 max-w-md">
        {message}
      </p>
      
      <p className="text-sm text-gray-500 mb-6 max-w-md">
        {getSuggestion()}
      </p>
      
      <div className="flex gap-3">
        {onRetry && (
          <Button
            onClick={onRetry}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Erneut versuchen
          </Button>
        )}
        
        {onGoHome && (
          <Button
            onClick={onGoHome}
            variant="secondary"
          >
            <Home className="h-4 w-4 mr-2" />
            Zur Startseite
          </Button>
        )}
      </div>
      
      {type === 'not-found' && (
        <div className="mt-8 p-4 bg-blue-50 rounded-lg max-w-md">
          <p className="text-sm text-blue-700">
            <strong>Tipp:</strong> Nicht alle Gemeinden haben bereits Energieanlagen. 
            Kontaktieren Sie uns, um zu erfahren, wie Ihre Gemeinde eine Energiegemeinschaft starten kann!
          </p>
        </div>
      )}
    </div>
  );
}